import React, { useState, useEffect } from 'react';
import { getSessionHistory, getSession } from '../../services/api';

// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlagIcon from '@mui/icons-material/Flag';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const SessionHistory = ({ token, conversationId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (token && conversationId) {
      loadSessionHistory();
    }
  }, [token, conversationId]);

  const loadSessionHistory = async () => {
    setLoading(true);
    setError('');
    
    try {
      const history = await getSessionHistory(conversationId, token);
      // Sort sessions by start time (newest first)
      const sortedSessions = [...history].sort((a, b) => {
        return new Date(b.startTime) - new Date(a.startTime);
      });
      setSessions(sortedSessions);
    } catch (err) {
      console.error('Error loading session history:', err);
      setError(err.message || 'Failed to load session history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSessionDetails = async (sessionId) => {
    setSelectedSession(sessionId);
    setDetailsLoading(true);
    setDetailsError('');
    setDialogOpen(true);
    
    try {
      const details = await getSession(conversationId, sessionId, token);
      setSessionDetails(details);
    } catch (err) {
      console.error('Error loading session details:', err);
      setDetailsError(err.message || 'Failed to load session details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Clear session details after dialog is closed with a small delay
    setTimeout(() => {
      setSessionDetails(null);
      setSelectedSession(null);
      setDetailsError('');
    }, 300); // Delay to allow dialog closing animation
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'In progress';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    }
    return `${minutes} min`;
  };

  // Render session details dialog
  const renderSessionDetailsDialog = () => {
    const session = sessionDetails?.session;
    
    return (
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Session Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailsError ? (
            <Alert severity="error">{detailsError}</Alert>
          ) : !session ? (
            <Alert severity="info">No session details available</Alert>
          ) : (
            <Box>
              {/* Session Overview */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Session Overview</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Typography variant="body1">
                      <Chip 
                        label={session.status === 'in_progress' ? 'In Progress' : 'Completed'} 
                        color={session.status === 'in_progress' ? 'primary' : 'success'} 
                        size="small"
                      />
                    </Typography>
                  </Box>
                  
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="body2" color="text.secondary">Start Time</Typography>
                    <Typography variant="body1">
                      {new Date(session.startTime).toLocaleString()}
                    </Typography>
                  </Box>
                  
                  {session.endTime && (
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary">End Time</Typography>
                      <Typography variant="body1">
                        {new Date(session.endTime).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="body2" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">
                      {session.duration} min ({formatDuration(session.startTime, session.endTime)})
                    </Typography>
                  </Box>
                  
                  {session.sessionNumber && (
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary">Session Number</Typography>
                      <Typography variant="body1">{session.sessionNumber}</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
              
              {/* Session Goals */}
              {session.goals && session.goals.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Session Goals</Typography>
                  <List dense disablePadding>
                    {session.goals.map((goal, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText 
                            primary={goal.description || 'Unknown Goal'}
                            secondary={`Type: ${goal.goalType || 'N/A'} • Status: ${goal.status || 'N/A'}`}
                          />
                        </ListItem>
                        {index < session.goals.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
              
              {/* Session Summary */}
              {(session.clientSummary || session.therapistSummary || (session.reflectionPrompts && session.reflectionPrompts.length > 0)) && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Session Summary</Typography>
                  
                  {session.clientSummary && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Client Summary</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                        {session.clientSummary}
                      </Typography>
                    </Box>
                  )}
                  
                  {session.therapistSummary && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Therapist Summary</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                        {session.therapistSummary}
                      </Typography>
                    </Box>
                  )}
                  
                  {session.reflectionPrompts && session.reflectionPrompts.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2">Reflection Prompts</Typography>
                      <List dense>
                        {session.reflectionPrompts.map((prompt, index) => (
                          <ListItem key={index} sx={{ pl: 0 }}>
                            <ListItemText 
                              primary={`${index + 1}. ${prompt}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Paper>
              )}
              
              {/* Session Messages */}
              {sessionDetails.messages && sessionDetails.messages.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Session Messages</Typography>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        View Conversation ({sessionDetails.messages.length} messages)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense sx={{ bgcolor: 'background.paper' }}>
                        {sessionDetails.messages.map((message, index) => (
                          <React.Fragment key={index}>
                            <ListItem alignItems="flex-start">
                              <ListItemText
                                primary={
                                  <Typography 
                                    component="span" 
                                    variant="body2"
                                    sx={{ 
                                      fontWeight: 'medium',
                                      color: message.senderRole === 'client' 
                                        ? 'primary.main' 
                                        : message.senderRole === 'human_therapist'
                                        ? 'secondary.main'
                                        : 'text.primary'
                                    }}
                                  >
                                    {message.senderRole === 'client' 
                                      ? 'Client' 
                                      : message.senderRole === 'human_therapist'
                                      ? 'Therapist'
                                      : 'AI'
                                    }
                                  </Typography>
                                }
                                secondary={
                                  <>
                                    <Typography
                                      component="span"
                                      variant="body2"
                                      color="text.primary"
                                      sx={{ display: 'block' }}
                                    >
                                      {message.text}
                                    </Typography>
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {new Date(message.timestamp).toLocaleTimeString()}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                            {index < sessionDetails.messages.length - 1 && <Divider component="li" />}
                          </React.Fragment>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Main render
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (sessions.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No therapy sessions found for this conversation.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <HistoryIcon sx={{ mr: 1 }} />
        Therapy Session History
      </Typography>
      
      <Paper variant="outlined">
        <List dense>
          {sessions.map((session, index) => (
            <React.Fragment key={session._id}>
              <ListItem sx={{ bgcolor: 'background.paper' }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2">
                        Session {session.sessionNumber || index + 1}
                      </Typography>
                      <Chip 
                        label={session.status === 'in_progress' ? 'In Progress' : 'Completed'} 
                        color={session.status === 'in_progress' ? 'primary' : 'success'} 
                        size="small"
                        sx={{ ml: 1 }}
                      />
                      {session.treatmentPhase && (
                        <Chip
                          label={session.treatmentPhase}
                          variant="outlined"
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(session.startTime).toLocaleDateString()} • {formatDuration(session.startTime, session.endTime)}
                        </Typography>
                      </Box>
                      
                      {session.goals && session.goals.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FlagIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {session.goals.length} {session.goals.length === 1 ? 'goal' : 'goals'}
                          </Typography>
                        </Box>
                      )}
                      
                      {(session.clientSummary || session.therapistSummary) && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ReceiptLongIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Summary available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => handleViewSessionDetails(session._id)}
                  sx={{ ml: 2 }}
                >
                  View Details
                </Button>
              </ListItem>
              {index < sessions.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
      
      {/* Session Details Dialog */}
      {renderSessionDetailsDialog()}
    </Box>
  );
};

export default SessionHistory;
